export const paths = {
  root: '/',
  programDetails: '/program-details',
  subjects: '/subjects',
  allSubjects: '/all-subjects',
  settings: '/settings',
  onboarding: '/onboarding',
  // Auth routes
  email: '/email',
  login: '/login',
  register: '/register',
  resetPassword: '/reset-password',
  // Enrollment routes
  enrollmentOverview: '/enrollment/overview',
  enrollmentApplication: '/enrollment/application',
  enrollmentWaitlist: '/enrollment/waitlist',
  enrollmentStatus: '/enrollment/status',
  enrollmentRejected: '/enrollment/status/rejected',
  enrollmentConfirmed: '/enrollment/status/confirmed',
  enrollmentAccessSuspended: '/enrollment/status/access-suspended',
  enrollmentMissedDeadline: '/enrollment/status/missed-deadline',
  enrollmentCheckout: '/enrollment/checkout',
  enrollmentCheckoutPayment: '/enrollment/checkout/payment',
  enrollmentUpdatePaymentMethod: '/enrollment/update-payment-method',
  accountUpdatePaymentMethod: '/account/update-payment-method',
  accountChangePaymentMethod: '/account/change-payment-method',
  postLogin: '/auth/post-login',
  welcome: '/welcome',
  events: '/events',
  allAiLabs: '/events?tab=ai_lab',
  allInstructors: '/all-instructors',
  saves: '/saves',
  resources: '/resources',
  resourcesForSubject: (subjectSlug: string) =>
    `/resources?subject=${encodeURIComponent(subjectSlug)}`,
  homepage: '/homepage',
  playground: '/playground',
  autoCheckout: '/auto-checkout',
  aiLab: (slug: string) => `/ai-labs/${encodeURIComponent(slug)}`,
  eventDetail: (slug: string) => `/events/${encodeURIComponent(slug)}`,
  eventCheckout: (slug: string) =>
    `/events/${encodeURIComponent(slug)}/checkout`,
  /** 3DS landing page; its only job is to post `3DS-authentication-complete`. */
  threeDsReturn: '/loading',
  subject: (subjectSlug: string) =>
    `/learning/subjects/${encodeURIComponent(subjectSlug)}`,
  module: (subjectSlug: string, moduleSlug: string | null = null) => {
    const base = `/learning/subjects/${encodeURIComponent(subjectSlug)}/modules`
    return moduleSlug ? `${base}/${encodeURIComponent(moduleSlug)}` : base
  },
  topic: (subjectSlug: string, moduleSlug: string, topicSlug: string) =>
    `/learning/subjects/${encodeURIComponent(
      subjectSlug,
    )}/modules/${encodeURIComponent(moduleSlug)}/topics/${encodeURIComponent(
      topicSlug,
    )}`,
}

/**
 * Absolute href for a receipt PDF download, routed through the proxy.
 *
 * The download is a plain `<a href download>` navigation (the proxy adds the
 * Bearer token server-side and preserves Content-Disposition), so the browser
 * must hit the Next.js app under its base path. Next.js only auto-applies
 * `basePath` to `<Link>`/router navigations and `next/image` — never to a
 * plain anchor's `href` — so it has to be prepended explicitly, the same way
 * every `fetch('/api/...')` in this app does.
 *
 * `basePath` is normalized the way `stripBasePath` (its inverse in the proxy)
 * treats it: a root-mounted app ('' or '/') and any trailing slash collapse to
 * no prefix, so we never emit a `//api/...` double slash.
 *
 * `paymentId` accepts a string because that is how `EducatorReceiptListItem`
 * serializes it — converting to a number at each call site would only add a
 * `NaN` path, and the id is interpolated into the URL either way.
 */
// Root ('' or '/') and any trailing slash collapse to no prefix, the way the
// proxy's `stripBasePath` (this prefix's inverse) treats it — never a `//api`.
const receiptsBasePathPrefix = (basePath: string) =>
  basePath === '/' ? '' : basePath.replace(/\/$/, '')

export const receiptDownloadUrl = (
  basePath: string,
  paymentId: string | number,
) =>
  `${receiptsBasePathPrefix(basePath)}/api/v3/educator/receipts/${paymentId}/download`

/**
 * Absolute href for the bulk-invoice zip download (Employer Reimbursement Kit),
 * routed through the proxy the same way as {@link receiptDownloadUrl}. The route
 * is extension-less and takes no `payment_id`: the backend returns a zip of every
 * eligible invoice for the authenticated learner.
 */
export const receiptsArchiveDownloadUrl = (basePath: string) =>
  `${receiptsBasePathPrefix(basePath)}/api/v3/educator/receipts/download`
